/**
 * secureStorage.js
 * A utility for securely storing sensitive data in the browser
 * Implements encryption for localStorage and sessionStorage
 */

import CryptoJS from 'crypto-js';

// Secret key for encryption - in production, this should be loaded from environment variables
// and not hardcoded in the source code
const SECRET_KEY = process.env.REACT_APP_STORAGE_SECRET_KEY || 'doctor-libya-default-key';

/**
 * Encrypts a value using AES encryption
 * @param {any} value - The value to encrypt
 * @returns {string} - The encrypted string
 */
const encrypt = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  
  const valueToEncrypt = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return CryptoJS.AES.encrypt(valueToEncrypt, SECRET_KEY).toString();
};

/**
 * Decrypts an encrypted value
 * @param {string} encryptedValue - The encrypted value
 * @returns {any} - The decrypted value
 */
const decrypt = (encryptedValue) => {
  if (!encryptedValue) {
    return encryptedValue;
  }
  
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY).toString(CryptoJS.enc.Utf8);
    
    // Try to parse as JSON, if it fails, return as string
    try {
      return JSON.parse(decrypted);
    } catch (e) {
      return decrypted;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

/**
 * Secure localStorage implementation
 */
export const secureLocalStorage = {
  /**
   * Sets an item in localStorage with encryption
   * @param {string} key - The key to store the value under
   * @param {any} value - The value to store
   */
  setItem: (key, value) => {
    const encryptedValue = encrypt(value);
    localStorage.setItem(key, encryptedValue);
  },
  
  /**
   * Gets an item from localStorage and decrypts it
   * @param {string} key - The key to retrieve
   * @returns {any} - The decrypted value
   */
  getItem: (key) => {
    const encryptedValue = localStorage.getItem(key);
    return decrypt(encryptedValue);
  },
  
  /**
   * Removes an item from localStorage
   * @param {string} key - The key to remove
   */
  removeItem: (key) => {
    localStorage.removeItem(key);
  },
  
  /**
   * Clears all items from localStorage
   */
  clear: () => {
    localStorage.clear();
  }
};

/**
 * Secure sessionStorage implementation
 */
export const secureSessionStorage = {
  /**
   * Sets an item in sessionStorage with encryption
   * @param {string} key - The key to store the value under
   * @param {any} value - The value to store
   */
  setItem: (key, value) => {
    const encryptedValue = encrypt(value);
    sessionStorage.setItem(key, encryptedValue);
  },
  
  /**
   * Gets an item from sessionStorage and decrypts it
   * @param {string} key - The key to retrieve
   * @returns {any} - The decrypted value
   */
  getItem: (key) => {
    const encryptedValue = sessionStorage.getItem(key);
    return decrypt(encryptedValue);
  },
  
  /**
   * Removes an item from sessionStorage
   * @param {string} key - The key to remove
   */
  removeItem: (key) => {
    sessionStorage.removeItem(key);
  },
  
  /**
   * Clears all items from sessionStorage
   */
  clear: () => {
    sessionStorage.clear();
  }
};

/**
 * Secure cookie implementation with encryption
 */
export const secureCookies = {
  /**
   * Sets a cookie with encryption
   * @param {string} name - The name of the cookie
   * @param {any} value - The value to store
   * @param {number} days - Number of days until the cookie expires
   * @param {boolean} secure - Whether the cookie should only be sent over HTTPS
   * @param {boolean} httpOnly - Whether the cookie should be accessible only through HTTP
   */
  setCookie: (name, value, days = 7, secure = true, sameSite = 'strict') => {
    const encryptedValue = encrypt(value);
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    const secureFlag = secure ? '; secure' : '';
    const sameSiteFlag = `; samesite=${sameSite}`;
    document.cookie = `${name}=${encryptedValue}; ${expires}; path=/${secureFlag}${sameSiteFlag}`;
  },
  
  /**
   * Gets a cookie and decrypts it
   * @param {string} name - The name of the cookie to retrieve
   * @returns {any} - The decrypted value
   */
  getCookie: (name) => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      
      if (cookie.indexOf(nameEQ) === 0) {
        const encryptedValue = cookie.substring(nameEQ.length, cookie.length);
        return decrypt(encryptedValue);
      }
    }
    
    return null;
  },
  
  /**
   * Deletes a cookie
   * @param {string} name - The name of the cookie to delete
   */
  deleteCookie: (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
};

/**
 * Utility to detect if the browser supports secure storage methods
 */
export const detectSecureStorageSupport = () => {
  const supportsLocalStorage = typeof localStorage !== 'undefined';
  const supportsSessionStorage = typeof sessionStorage !== 'undefined';
  const supportsCookies = navigator.cookieEnabled;
  
  return {
    localStorage: supportsLocalStorage,
    sessionStorage: supportsSessionStorage,
    cookies: supportsCookies
  };
};

/**
 * Default export with all secure storage methods
 */
export default {
  local: secureLocalStorage,
  session: secureSessionStorage,
  cookie: secureCookies,
  detectSupport: detectSecureStorageSupport
};